import * as React from "react";
import { cn } from "../utils/cn";
import { Avatar, AvatarGradientFallback } from "./avatar";
import { getGradientForId } from "./OrgAvatar";
import { Mono, Text } from "../primitives/typography";

/**
 * A person, as they appear in the first column of every list that shows people.
 *
 * Extracted because this exact block — a gradient avatar keyed on the id, the name above, the email
 * in mono beneath — was written out by hand in the platform Users directory, an organization's
 * members tab, an assignment's learners, the add-member picker and the push-notification audience.
 * It had already drifted: `AssignmentUsers` renders `AvatarGradientFallback` with no `from`/`to`, so
 * its avatars were grey while everyone else's were colored.
 *
 * Deliberately *not* part of a shared user table. The three tables that use it share this column and
 * nothing else — the rest are org/role/status, phone/license/joined and due/progress — so a table
 * component covering all three would need a prop per difference. The cell is the reusable part.
 */
export interface UserCellProps extends React.ComponentProps<"div"> {
    /** Seeds the avatar gradient, so one person is the same color everywhere. */
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    /** Already-joined display name, for callers that never had the parts separately. */
    name?: string | null;
    email?: string | null;
    /** Rendered under the email — "Invited", a role, whatever the list needs. */
    meta?: React.ReactNode;
}

/**
 * First letter of each name part, falling back to the email when somebody has neither.
 *
 * Takes a joined name too, because one caller only ever had that — and its avatars were the grey
 * no-gradient fallback precisely because it could not produce initials the same way as the others.
 */
const initialsFor = (
    firstName?: string | null,
    lastName?: string | null,
    email?: string | null,
    name?: string | null,
): string => {
    const parts = name?.trim()
        ? name.trim().split(/\s+/)
        : [
              firstName ?? "",
              lastName ?? "",
          ];
    const initials = `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`;
    if (initials) return initials.toUpperCase();
    return (email?.trim()?.[0] ?? "?").toUpperCase();
};

const UserCell = ({ id, firstName, lastName, name, email, meta, className, ...props }: UserCellProps) => {
    const [
        from,
        to,
    ] = getGradientForId(id);
    const displayName = name?.trim() || `${firstName ?? ""} ${lastName ?? ""}`.trim();

    return (
        <div className={cn("flex items-center gap-3", className)} {...props}>
            <Avatar size="sm" className="shrink-0">
                <AvatarGradientFallback from={from} to={to}>
                    {initialsFor(firstName, lastName, email, name)}
                </AvatarGradientFallback>
            </Avatar>
            <div className="min-w-0">
                {/* Falls back to the email as the name: a row with a blank first line reads as a
                    broken record rather than as a person who has not filled in their profile. */}
                <Text size="sm" weight="semibold" className="truncate">
                    {displayName || email || "—"}
                </Text>
                {displayName && email && (
                    <Mono size="xs" muted className="truncate">
                        {email}
                    </Mono>
                )}
                {meta}
            </div>
        </div>
    );
};

export { UserCell, initialsFor };
